import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StageFormDialog } from '~/components/features/workflow/stage-form-dialog';
import {
  WorkflowStageRow,
  type StageItem
} from '~/components/features/workflow/workflow-stage-row';
import { PageContainer } from '~/components/layouts/page-container';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '~/components/ui/alert-dialog';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';
import { resolveSlugIds } from '~/lib/slug-resolver';
import { stagesQueryOptions } from '~/lib/workflow-stages';

export const Route = createFileRoute('/_authenticated/org/$orgSlug/department/$deptSlug/workflow/')(
  {
    loader: ({ context, params }) => {
      const { orgId, deptId } = resolveSlugIds(
        context.queryClient,
        params.orgSlug,
        params.deptSlug
      );
      if (orgId && deptId) {
        void context.queryClient.prefetchQuery(stagesQueryOptions(orgId, deptId));
      }
    },
    component: WorkflowPage
  }
);

function WorkflowPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('workflows.title'));
  const queryClient = useQueryClient();
  const { orgSlug, deptSlug } = Route.useParams();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<StageItem | null>(null);
  const [deletingStage, setDeletingStage] = useState<StageItem | null>(null);

  const { data: stagesData, isLoading } = useQuery({
    ...stagesQueryOptions(orgId, deptId ?? ''),
    enabled: !!orgId && !!deptId
  });

  const stages = stagesData?.stages ?? [];
  const sortedStages = [...stages].sort((a, b) => a.position - b.position);
  const systemStages = sortedStages.filter((s) => s.isSystem);
  const customStages = sortedStages.filter((s) => !s.isSystem);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const invalidateStages = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['workflow-stages', orgId, deptId] });
  }, [queryClient, orgId, deptId]);

  const createStageMutation = useMutation({
    mutationFn: async (input: { name: string; color: string; position: number }) => {
      if (!deptId) throw new Error(i18n.t('errors.unexpected'));
      const response = await api.api.org[':orgId'].department[':deptId']['workflow-stages'].$post({
        param: { orgId, deptId },
        json: input
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    },
    onSuccess: invalidateStages
  });

  const updateStageMutation = useMutation({
    mutationFn: async ({
      stageId,
      input
    }: {
      stageId: string;
      input: { name?: string; color?: string };
    }) => {
      if (!deptId) throw new Error(i18n.t('errors.unexpected'));
      const response = await api.api.org[':orgId'].department[':deptId']['workflow-stages'][
        ':stageId'
      ].$patch({
        param: { orgId, deptId, stageId },
        json: input
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    },
    onSuccess: invalidateStages
  });

  const deleteStageMutation = useMutation({
    mutationFn: async (stageId: string) => {
      if (!deptId) throw new Error(i18n.t('errors.unexpected'));
      const response = await api.api.org[':orgId'].department[':deptId']['workflow-stages'][
        ':stageId'
      ].$delete({
        param: { orgId, deptId, stageId }
      });
      if (!response.ok) {
        const body = (await response.json()) as { error?: string };
        throw new Error(body.error ?? i18n.t('errors.unexpected'));
      }
      return response.json();
    },
    onSuccess: invalidateStages
  });

  const reorderStagesMutation = useMutation({
    mutationFn: async (positions: Array<{ id: string; position: number }>) => {
      if (!deptId) throw new Error(i18n.t('errors.unexpected'));
      const response = await api.api.org[':orgId'].department[':deptId']['workflow-stages'].$patch({
        param: { orgId, deptId },
        json: positions
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    },
    onSuccess: invalidateStages
  });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = customStages.findIndex((s) => s.id === active.id);
      const newIndex = customStages.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...customStages];
      const [moved] = reordered.splice(oldIndex, 1);
      if (moved) reordered.splice(newIndex, 0, moved);

      const offset = systemStages.length;
      reorderStagesMutation.mutate(reordered.map((s, i) => ({ id: s.id, position: offset + i })));
    },
    [customStages, systemStages.length, reorderStagesMutation]
  );

  const handleCreate = useCallback(
    (name: string, color: string) => {
      createStageMutation.mutate({ name, color, position: stages.length });
      setAddDialogOpen(false);
    },
    [createStageMutation, stages.length]
  );

  const handleUpdate = useCallback(
    (name: string, color: string) => {
      if (!editingStage) return;
      updateStageMutation.mutate({ stageId: editingStage.id, input: { name, color } });
      setEditingStage(null);
    },
    [editingStage, updateStageMutation]
  );

  const handleDelete = useCallback(() => {
    if (!deletingStage) return;
    deleteStageMutation.mutate(deletingStage.id);
    setDeletingStage(null);
  }, [deletingStage, deleteStageMutation]);

  return (
    <PageContainer
      breadcrumbs={[{ label: t('workflows.title') }]}
      title={t('workflows.title')}
      subtitle={t('workflows.description')}
      actions={
        <Button
          onClick={() => {
            setAddDialogOpen(true);
          }}
        >
          <Plus className="size-4" />
          {t('workflows.stages.addStage')}
        </Button>
      }
    >
      <div>
        <h2 className="text-lg font-semibold">{t('workflows.stages.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('workflows.stages.subtitle')}</p>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {systemStages.map((stage) => (
            <WorkflowStageRow
              key={stage.id}
              stage={stage}
              onEdit={setEditingStage}
              onDelete={setDeletingStage}
            />
          ))}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={customStages.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {customStages.map((stage) => (
                <WorkflowStageRow
                  key={stage.id}
                  stage={stage}
                  onEdit={setEditingStage}
                  onDelete={setDeletingStage}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <StageFormDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        title={t('workflows.stages.addStage')}
        onSubmit={handleCreate}
        isPending={createStageMutation.isPending}
        error={createStageMutation.error?.message ?? null}
      />

      <StageFormDialog
        open={!!editingStage}
        onOpenChange={(v) => {
          if (!v) setEditingStage(null);
        }}
        title={t('workflows.stages.editStage')}
        defaultName={editingStage?.name}
        defaultColor={editingStage?.color ?? undefined}
        onSubmit={handleUpdate}
        isPending={updateStageMutation.isPending}
      />

      <AlertDialog
        open={!!deletingStage}
        onOpenChange={(v) => {
          if (!v) setDeletingStage(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workflows.stages.deleteStage')}</AlertDialogTitle>
            <AlertDialogDescription>{t('workflows.stages.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={deleteStageMutation.isPending}
            >
              {t('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
